import React, { useEffect, useState } from "react";
import { Store, Search, Plus, Pencil, Copy, Trash2, ArrowUp, ArrowDown, ShieldCheck, History, X, PackageX, PackageCheck, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import CatalogImporter from "../catalog/CatalogImporter";

const TYPES = ["prodotto", "servizio", "prenotazione", "esperienza", "corso", "evento", "abbonamento"];
const SERVICE_TYPES = ["servizio", "prenotazione", "esperienza", "corso"];
const empty = { type: "prodotto", name: "", description: "", price: "", category: "", images: [], availability: "", duration_min: "", max_participants: "", quantity: "", active: true, variants: [] };

export default function OpsCatalogPanel() {
  const [businesses, setBusinesses] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null); // {id, name, category}
  const [onBehalf, setOnBehalf] = useState(false);
  const [items, setItems] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [form, setForm] = useState(null); // editing item or new
  const [importing, setImporting] = useState(false);

  useEffect(() => { api.get("/ops/businesses").then((r) => setBusinesses(r.data || [])).catch(() => {}); }, []);

  const loadCatalog = (bid) => {
    api.get(`/ops/business/${bid}/catalog`).then((r) => { setItems(r.data.items || []); }).catch(() => toast.error("Errore nel caricamento catalogo"));
    api.get(`/ops/business/${bid}/catalog/audit`).then((r) => setAudit(r.data || [])).catch(() => {});
  };
  const pick = (b) => { setSel(b); setOnBehalf(false); setForm(null); setShowAudit(false); loadCatalog(b.id); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Il nome è obbligatorio");
    const payload = { ...form, price: form.price === "" ? 0 : Number(form.price),
      duration_min: form.duration_min === "" ? null : Number(form.duration_min),
      max_participants: form.max_participants === "" ? null : Number(form.max_participants),
      quantity: form.quantity === "" ? null : Number(form.quantity) };
    try {
      if (form._id) await api.put(`/ops/business/${sel.id}/catalog/${form._id}`, payload);
      else await api.post(`/ops/business/${sel.id}/catalog`, payload);
      toast.success("Salvato (per conto dell'attività)"); setForm(null); loadCatalog(sel.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
  };
  const toggleAvail = async (it) => {
    try { await api.patch(`/ops/business/${sel.id}/catalog/${it._id}/availability`, { active: !it.active }); loadCatalog(sel.id); }
    catch { toast.error("Errore"); }
  };
  const duplicate = async (it) => { try { await api.post(`/ops/business/${sel.id}/catalog/${it._id}/duplicate`); toast.success("Duplicato"); loadCatalog(sel.id); } catch { toast.error("Errore"); } };
  const remove = async (it) => { if (!window.confirm(`Eliminare "${it.name}"?`)) return; try { await api.delete(`/ops/business/${sel.id}/catalog/${it._id}`); loadCatalog(sel.id); } catch { toast.error("Errore"); } };
  const move = async (idx, dir) => {
    const arr = [...items]; const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; setItems(arr);
    try { await api.put(`/ops/business/${sel.id}/catalog-reorder`, { order: arr.map((x) => x._id) }); } catch { toast.error("Errore riordino"); }
  };

  // --- Business picker ---
  if (!sel) {
    const filtered = businesses.filter((b) => !q || (b.name || "").toLowerCase().includes(q.toLowerCase()));
    return (
      <div data-testid="ops-catalog-picker">
        <div className="flex items-center gap-2 mb-4"><Store className="h-5 w-5 text-[#00E676]" /><h3 className="font-semibold text-lg">Menu / Catalogo Attività</h3></div>
        <p className="text-sm text-zinc-400 mb-4">Assisti un'attività nella configurazione del catalogo. Ogni modifica viene registrata come fatta dalla Centrale.</p>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <input data-testid="ops-biz-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca attività…"
            className="w-full bg-[#0A0A0C] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {filtered.map((b) => (
            <button key={b.id} data-testid={`ops-biz-${b.id}`} onClick={() => pick(b)}
              className="text-left rounded-xl bg-[#1C1C1F] border border-white/10 hover:border-[#00E676]/40 p-3 transition-colors">
              <div className="font-semibold text-sm">{b.name}</div>
              <div className="text-xs text-zinc-500">{b.category || "—"} · {b.city || ""} · {b.status || ""}</div>
            </button>
          ))}
          {filtered.length === 0 && <div className="text-zinc-500 text-sm">Nessuna attività trovata.</div>}
        </div>
      </div>
    );
  }

  // --- Catalog editor for selected business ---
  return (
    <div data-testid="ops-catalog-editor">
      <button data-testid="ops-back-businesses" onClick={() => setSel(null)} className="flex items-center gap-1 text-sm text-zinc-400 mb-3"><ArrowLeft className="h-4 w-4" /> Tutte le attività</button>

      <div className="rounded-xl border border-[#FF9F45]/40 bg-[#FF9F45]/10 p-3 mb-4 flex items-center gap-3" data-testid="ops-onbehalf-banner">
        <ShieldCheck className="h-5 w-5 text-[#FF9F45] shrink-0" />
        <div className="flex-1 min-w-0 text-sm">
          Stai agendo <b>per conto di</b> <span className="text-[#FF9F45] font-semibold">{sel.name}</span>. Le modifiche sono tracciate e visibili al titolare.
        </div>
        {!onBehalf ? (
          <button data-testid="ops-enable-edit" onClick={() => setOnBehalf(true)} className="text-xs font-semibold bg-[#FF9F45] text-black rounded-full px-3 py-1.5 shrink-0">Modifica per conto dell'attività</button>
        ) : (
          <span className="text-[10px] font-bold text-[#FF9F45] uppercase shrink-0">Modifica attiva</span>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Catalogo · {items.length} elementi</h3>
        <div className="flex gap-2">
          <button data-testid="ops-audit-toggle" onClick={() => setShowAudit((s) => !s)} className="flex items-center gap-1 text-xs text-zinc-300 border border-white/15 rounded-full px-3 py-1.5"><History className="h-3.5 w-3.5" /> Storico</button>
          {onBehalf && <button data-testid="ops-import-ai" onClick={() => setImporting(true)} className="flex items-center gap-1 text-xs font-semibold bg-gradient-to-r from-[#7DF9FF] to-[#00E676] text-black rounded-full px-3 py-1.5"><Sparkles className="h-3.5 w-3.5" /> Importa AI</button>}
          {onBehalf && <button data-testid="ops-add-item" onClick={() => setForm({ ...empty, type: SERVICE_TYPES.includes((sel.category || "").toLowerCase()) ? "servizio" : "prodotto" })} className="flex items-center gap-1 text-xs font-semibold bg-[#00E676] text-black rounded-full px-3 py-1.5"><Plus className="h-3.5 w-3.5" /> Nuovo</button>}
        </div>
      </div>
      {importing && sel && (
        <CatalogImporter parseUrl={`/ops/business/${sel.id}/catalog/import/parse`} commitUrl={`/ops/business/${sel.id}/catalog/import/commit`}
          onClose={() => setImporting(false)} onDone={() => { setImporting(false); loadCatalog(sel.id); }} />
      )}

      {showAudit && (
        <div className="mb-4 rounded-xl bg-[#0A0A0C] border border-white/10 p-3 max-h-56 overflow-y-auto" data-testid="ops-audit-list">
          {audit.length === 0 && <div className="text-xs text-zinc-500">Nessuna modifica registrata.</div>}
          {audit.map((a, i) => (
            <div key={i} className="text-xs text-zinc-400 border-b border-white/5 py-1.5">
              <span className="text-[#FF9F45] font-semibold">{a.actor_name}</span> · {a.action} · {a.after?.name || a.before?.name || ""}
              <span className="text-zinc-600"> · {a.at ? new Date(a.at).toLocaleString("it-IT") : ""}</span>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={it._id} data-testid={`ops-item-${it._id}`} className={`rounded-xl border p-3 flex items-center gap-3 ${it.active ? "border-white/10 bg-[#1C1C1F]" : "border-red-500/30 bg-red-500/5"}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-2">{it.name}<span className="text-[10px] text-zinc-500 uppercase">{it.type}</span>{!it.active && <span className="text-[10px] text-red-400 font-bold">ESAURITO</span>}</div>
              <div className="text-xs text-zinc-500">€{Number(it.price || 0).toFixed(2)}{it.category ? ` · ${it.category}` : ""}{it.duration_min ? ` · ${it.duration_min} min` : ""}{it.max_participants ? ` · ${it.max_participants} posti` : ""}</div>
            </div>
            {onBehalf && <>
              <button title="Su" onClick={() => move(idx, -1)} className="text-zinc-500 hover:text-white p-1"><ArrowUp className="h-4 w-4" /></button>
              <button title="Giù" onClick={() => move(idx, 1)} className="text-zinc-500 hover:text-white p-1"><ArrowDown className="h-4 w-4" /></button>
              <button title={it.active ? "Segna esaurito" : "Rendi disponibile"} data-testid={`ops-avail-${it._id}`} onClick={() => toggleAvail(it)} className="text-zinc-400 hover:text-white p-1">{it.active ? <PackageX className="h-4 w-4" /> : <PackageCheck className="h-4 w-4 text-[#00E676]" />}</button>
              <button title="Duplica" onClick={() => duplicate(it)} className="text-zinc-400 hover:text-white p-1"><Copy className="h-4 w-4" /></button>
              <button title="Modifica" data-testid={`ops-edit-${it._id}`} onClick={() => setForm({ ...empty, ...it, price: it.price ?? "", duration_min: it.duration_min ?? "", max_participants: it.max_participants ?? "", quantity: it.quantity ?? "" })} className="text-zinc-400 hover:text-[#00E676] p-1"><Pencil className="h-4 w-4" /></button>
              <button title="Elimina" onClick={() => remove(it)} className="text-zinc-400 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button>
            </>}
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 text-sm">Catalogo vuoto.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3" onClick={() => setForm(null)}>
          <div className="bg-[#1C1C1F] border border-white/12 rounded-2xl p-4 max-w-lg w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ops-item-form">
            <div className="flex items-center justify-between mb-3"><h4 className="font-semibold">{form._id ? "Modifica elemento" : "Nuovo elemento"}</h4><button onClick={() => setForm(null)}><X className="h-5 w-5 text-zinc-400" /></button></div>
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-zinc-400">Tipo
                  <select data-testid="form-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1.5 text-sm">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="text-xs text-zinc-400">Prezzo €
                  <input data-testid="form-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1.5 text-sm" />
                </label>
              </div>
              <label className="text-xs text-zinc-400 block">Nome
                <input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs text-zinc-400 block">Descrizione
                <textarea data-testid="form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1.5 text-sm" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-zinc-400">Categoria
                  <input data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1.5 text-sm" />
                </label>
                <label className="text-xs text-zinc-400">Immagine (URL)
                  <input data-testid="form-image" value={(form.images || [])[0] || ""} onChange={(e) => setForm({ ...form, images: e.target.value ? [e.target.value] : [] })} className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1.5 text-sm" />
                </label>
              </div>
              {SERVICE_TYPES.includes(form.type) && (
                <div className="grid grid-cols-3 gap-2">
                  <label className="text-xs text-zinc-400">Durata (min)
                    <input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1.5 text-sm" />
                  </label>
                  <label className="text-xs text-zinc-400">Posti
                    <input type="number" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1.5 text-sm" />
                  </label>
                  <label className="text-xs text-zinc-400">Disponibilità
                    <input value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} placeholder="es. Lun-Ven" className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1.5 text-sm" />
                  </label>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Disponibile (deseleziona = esaurito)</label>
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid="form-save" onClick={save} className="flex-1 bg-[#00E676] text-black font-semibold rounded-lg py-2">Salva per conto dell'attività</button>
              <button onClick={() => setForm(null)} className="px-4 rounded-lg border border-white/15 text-sm">Annulla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
